import React, { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, CalendarCheck, Play, ExternalLink, Download, Check, User } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
function fmtWeekday(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "long" }); } catch { return ""; }
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); } catch { return ""; }
}

function buildGoogleUrl(sched) {
  if (!sched?.scheduled_start) return "#";
  const s = new Date(sched.scheduled_start).toISOString().replace(/[-:]|\.\d{3}/g, "");
  const e = new Date(sched.scheduled_end).toISOString().replace(/[-:]|\.\d{3}/g, "");
  const title = encodeURIComponent(`Credit Quack — ${sched.operation_type}`);
  const details = encodeURIComponent(`Assigned specialist: ${sched.assigned_user_name || "Credit Quack Team"}`);
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${s}/${e}&details=${details}`;
}

function buildOutlookUrl(sched) {
  if (!sched?.scheduled_start) return "#";
  const s = new Date(sched.scheduled_start).toISOString();
  const e = new Date(sched.scheduled_end).toISOString();
  const title = encodeURIComponent(`Credit Quack — ${sched.operation_type}`);
  const body = encodeURIComponent(`Assigned specialist: ${sched.assigned_user_name || "Credit Quack Team"}`);
  return `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${title}&startdt=${s}&enddt=${e}&body=${body}`;
}

function icsUrl(sched) {
  return `${API}/scheduling/schedules/${sched.id}/ics`;
}

export default function ClientScheduleCard({ onChange }) {
  const [state, setState] = useState({ pending: null, confirmed: [] });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/client/schedules/pending");
      setState(data || { pending: null, confirmed: [] });
      if (data?.pending?.proposed_options?.length && !selected) {
        setSelected(data.pending.proposed_options[0].id);
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const confirm = async () => {
    if (!state.pending || !selected) return;
    setBusy(true);
    try {
      await api.post(`/client/schedules/${state.pending.id}/confirm`, { option_id: selected });
      toast.success("You're scheduled!");
      setSelected(null);
      await load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not confirm — please try another time.");
      await load();
    } finally { setBusy(false); }
  };

  const p = state.pending;
  const confirmedList = state.confirmed || [];

  if (!p && confirmedList.length === 0) return null;

  return (
    <div className="mb-6 space-y-4" data-testid="client-schedule-section">
      {p && (
        <Card className="p-5 sm:p-6 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-sm" data-testid="client-pending-schedule">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center">
              <CalendarClock size={20} />
            </div>
            <Badge className="bg-amber-500 text-white uppercase text-[10px] tracking-widest">Action needed</Badge>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-black text-slate-900 mt-2">Choose your processing time</h3>
          <p className="text-slate-600 mt-1">Your Credit Quack team is ready for the next step on your file. Select one of the available times below.</p>
          <p className="text-xs text-slate-500 mt-2">
            <span className="font-semibold text-slate-700">{p.operation_type}</span>
            {p.assigned_user_name && <> · with <span className="font-semibold text-slate-700">{p.assigned_user_name}</span></>}
            {" · "}~{p.duration_minutes} min
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
            {(p.proposed_options || []).map((opt) => {
              const isSelected = selected === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setSelected(opt.id)}
                  className={`text-left rounded-2xl border-2 p-4 transition ${isSelected ? "border-amber-500 bg-amber-500 text-white shadow-lg scale-[1.02]" : "border-slate-200 bg-white text-slate-900 hover:border-amber-300 hover:bg-amber-50"}`}
                  data-testid={`client-option-${opt.id}`}
                >
                  <div className={`text-xs uppercase tracking-widest font-bold ${isSelected ? "text-white/80" : "text-slate-400"}`}>{fmtWeekday(opt.start_iso)}</div>
                  <div className="font-display text-2xl font-black mt-1">{fmtTime(opt.start_iso)}</div>
                  <div className={`text-xs mt-1 ${isSelected ? "text-white/70" : "text-slate-500"}`}>
                    {new Date(opt.start_iso).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    {isSelected && <> · <span className="font-bold">Selected</span></>}
                  </div>
                </button>
              );
            })}
          </div>

          {p.notes && <div className="mt-4 rounded-xl bg-white border border-slate-100 p-3 text-sm text-slate-600"><span className="font-semibold">Note from team:</span> {p.notes}</div>}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <Button onClick={confirm} disabled={!selected || busy} className="bg-amber-500 hover:bg-amber-600 text-white h-12 rounded-xl text-base font-semibold flex-1" data-testid="client-confirm-time">
              <Check size={18} className="mr-2" /> {busy ? "Confirming…" : "Confirm Time"}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center">Times are held until you confirm. First-come, first-served.</p>
        </Card>
      )}

      {confirmedList.map((c) => (
        <Card key={c.id} className="p-5 sm:p-6 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white" data-testid={`client-confirmed-${c.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
              {c.status === "in_progress" ? <Play size={20} /> : <CalendarCheck size={20} />}
            </div>
            <Badge className={`${c.status === "in_progress" ? "bg-blue-500" : "bg-emerald-500"} text-white uppercase text-[10px] tracking-widest`}>
              {c.status === "in_progress" ? "In Progress" : "You're Scheduled"}
            </Badge>
          </div>
          <h3 className="font-display text-2xl font-black text-slate-900 mt-1">{fmt(c.scheduled_start)}</h3>
          <p className="text-slate-600 mt-1">
            {c.status === "in_progress"
              ? `Your Credit Quack team is working on your file right now.`
              : `Your Credit Quack team has reserved this time to work on your file.`}
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div className="rounded-xl bg-white border border-slate-100 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Assigned specialist</p>
              <p className="font-semibold text-slate-900 mt-0.5 flex items-center gap-1"><User size={14} className="text-slate-400" /> {c.assigned_user_name || "Credit Quack Team"}</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-100 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400">What's being done</p>
              <p className="font-semibold text-slate-900 mt-0.5">{c.operation_type}</p>
            </div>
          </div>
          {c.requires_client_on_call
            ? <p className="text-xs mt-3 text-amber-700 font-semibold">📞 You'll need to be available on a call for this one.</p>
            : <p className="text-xs mt-3 text-slate-500">You don't need to be on a call — the team is processing your file.</p>}

          {c.status !== "in_progress" && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">Add to your calendar</p>
              <div className="flex flex-wrap gap-2">
                <a href={buildGoogleUrl(c)} target="_blank" rel="noopener noreferrer" className="text-xs" data-testid={`add-google-${c.id}`}>
                  <Button size="sm" variant="outline" className="rounded-xl"><ExternalLink size={12} className="mr-1" /> Google</Button>
                </a>
                <a href={buildOutlookUrl(c)} target="_blank" rel="noopener noreferrer" className="text-xs" data-testid={`add-outlook-${c.id}`}>
                  <Button size="sm" variant="outline" className="rounded-xl"><ExternalLink size={12} className="mr-1" /> Outlook</Button>
                </a>
                <a href={icsUrl(c)} className="text-xs" data-testid={`add-apple-${c.id}`}>
                  <Button size="sm" variant="outline" className="rounded-xl"><Download size={12} className="mr-1" /> Apple / .ics</Button>
                </a>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
